import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, SimpleGrid, Spinner,
  Stat, StatLabel, StatNumber, StatHelpText,
  Badge, Icon, Alert, AlertIcon, Button, Code, Divider,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td,
  Textarea, useToast, Tooltip, Progress,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiDatabase, FiActivity, FiPlay,
  FiHardDrive, FiClock, FiCheckCircle, FiAlertTriangle,
} from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

// ── Overview Tab ────────────────────────────────────────────────────────────

function OverviewTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/postgres/overview/`, { headers: getAuthHeaders() })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => { if (data.error) throw new Error(data.error); setOverview(data); setError(null); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  if (loading) return <Box textAlign="center" py={16}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return (
    <Alert status="error" borderRadius="lg"><AlertIcon />
      <VStack align="start" spacing={1}>
        <Text fontWeight="bold">Failed to connect to PostgreSQL</Text>
        <Text fontSize="sm">{error}</Text>
      </VStack>
    </Alert>
  );

  const o = overview;
  const connPct = o.max_connections > 0 ? Math.round((o.connections.total / o.max_connections) * 100) : 0;

  return (
    <Box>
      <HStack justify="flex-end" mb={6}>
        <Button leftIcon={<FiRefreshCw />} size="sm" onClick={fetch_}>Refresh</Button>
      </HStack>

      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="blue.400">
          <HStack mb={3}>
            <Icon as={FiDatabase} boxSize={5} color="blue.500" />
            <Text fontWeight="bold" color="gray.600" fontSize="sm">Database</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="2xl">{o.database}</StatNumber>
            <StatHelpText>{o.database_size}</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="green.400">
          <HStack mb={3}>
            <Icon as={FiCheckCircle} boxSize={5} color="green.500" />
            <Text fontWeight="bold" color="gray.600" fontSize="sm">Cache Hit Ratio</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="2xl" color={o.cache_hit_ratio >= 95 ? 'green.600' : o.cache_hit_ratio >= 80 ? 'yellow.600' : 'red.600'}>
              {o.cache_hit_ratio}%
            </StatNumber>
            <StatHelpText>Target: ≥ 95%</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="purple.400">
          <HStack mb={3}>
            <Icon as={FiActivity} boxSize={5} color="purple.500" />
            <Text fontWeight="bold" color="gray.600" fontSize="sm">Connections</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="2xl">{o.connections.total} / {o.max_connections}</StatNumber>
            <StatHelpText>{connPct}% used</StatHelpText>
          </Stat>
          <Progress value={connPct} colorScheme={connPct > 80 ? 'red' : connPct > 50 ? 'yellow' : 'green'} size="sm" borderRadius="full" mt={2} />
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="orange.400">
          <HStack mb={3}>
            <Icon as={FiAlertTriangle} boxSize={5} color="orange.500" />
            <Text fontWeight="bold" color="gray.600" fontSize="sm">Dead Tuples</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="2xl">{o.tuple_stats.dead_tuples.toLocaleString()}</StatNumber>
            <StatHelpText>{o.tuple_stats.live_tuples.toLocaleString()} live</StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* Detail panels */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="md" mb={4} color="gray.700">Connection Breakdown</Heading>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Active</Text>
              <Badge colorScheme="green" fontSize="md" px={3} py={1}>{o.connections.active}</Badge>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Idle</Text>
              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>{o.connections.idle}</Badge>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Idle in Transaction</Text>
              <Badge colorScheme={o.connections.idle_in_transaction > 0 ? 'yellow' : 'gray'} fontSize="md" px={3} py={1}>
                {o.connections.idle_in_transaction}
              </Badge>
            </HStack>
          </VStack>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="md" mb={4} color="gray.700">Transaction Stats</Heading>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Committed</Text>
              <Text fontWeight="bold">{o.transactions.committed?.toLocaleString()}</Text>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Rolled Back</Text>
              <Text fontWeight="bold" color={o.transactions.rolled_back > 0 ? 'red.500' : undefined}>
                {o.transactions.rolled_back?.toLocaleString()}
              </Text>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Rows Inserted</Text>
              <Text fontWeight="bold">{o.transactions.rows_inserted?.toLocaleString()}</Text>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Rows Updated</Text>
              <Text fontWeight="bold">{o.transactions.rows_updated?.toLocaleString()}</Text>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Rows Deleted</Text>
              <Text fontWeight="bold">{o.transactions.rows_deleted?.toLocaleString()}</Text>
            </HStack>
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Server info */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Heading size="md" mb={4} color="gray.700">Server Info</Heading>
        <Code display="block" whiteSpace="pre-wrap" p={4} borderRadius="md" bg="gray.800" color="green.300" fontSize="sm">
          {o.version}
        </Code>
        <Text fontSize="sm" color="gray.500" mt={3}>
          <Icon as={FiClock} boxSize={3} mr={1} />
          Running since {o.start_time ? new Date(o.start_time).toLocaleString() : '—'}
        </Text>
      </Box>
    </Box>
  );
}


// ── Tables Tab ──────────────────────────────────────────────────────────────

function TablesTab() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/postgres/tables/`, { headers: getAuthHeaders() })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => { if (data.error) throw new Error(data.error); setTables(data.tables); setError(null); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  if (loading) return <Box textAlign="center" py={16}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text color="gray.500">{tables.length} tables</Text>
        <Button leftIcon={<FiRefreshCw />} size="sm" onClick={fetch_}>Refresh</Button>
      </HStack>
      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="auto">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Table</Th>
              <Th isNumeric>Rows</Th>
              <Th>Total Size</Th>
              <Th>Table Size</Th>
              <Th>Index Size</Th>
              <Th isNumeric>Dead Tuples</Th>
              <Th isNumeric>Seq Scans</Th>
              <Th isNumeric>Idx Scans</Th>
              <Th>Last Vacuum</Th>
              <Th>Last Analyze</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tables.map(t => (
              <Tr key={t.table_name} _hover={{ bg: 'gray.50' }}>
                <Td>
                  <Text fontWeight="medium" fontSize="sm" fontFamily="mono">{t.table_name}</Text>
                </Td>
                <Td isNumeric><Text fontSize="sm">{t.row_count?.toLocaleString()}</Text></Td>
                <Td><Badge colorScheme="blue" fontSize="xs">{t.total_size}</Badge></Td>
                <Td><Text fontSize="xs" color="gray.500">{t.table_size}</Text></Td>
                <Td><Text fontSize="xs" color="gray.500">{t.index_size}</Text></Td>
                <Td isNumeric>
                  <Text fontSize="sm" color={t.dead_tuples > 1000 ? 'red.500' : t.dead_tuples > 100 ? 'yellow.600' : 'gray.600'}>
                    {t.dead_tuples?.toLocaleString()}
                  </Text>
                </Td>
                <Td isNumeric><Text fontSize="xs" color="gray.500">{t.seq_scan?.toLocaleString()}</Text></Td>
                <Td isNumeric><Text fontSize="xs" color="gray.500">{t.idx_scan?.toLocaleString()}</Text></Td>
                <Td>
                  <Tooltip label={t.last_autovacuum ? `Auto: ${new Date(t.last_autovacuum).toLocaleString()}` : 'No autovacuum'}>
                    <Text fontSize="xs" color="gray.500">
                      {t.last_vacuum ? new Date(t.last_vacuum).toLocaleDateString() : t.last_autovacuum ? 'auto' : '—'}
                    </Text>
                  </Tooltip>
                </Td>
                <Td>
                  <Tooltip label={t.last_autoanalyze ? `Auto: ${new Date(t.last_autoanalyze).toLocaleString()}` : 'No autoanalyze'}>
                    <Text fontSize="xs" color="gray.500">
                      {t.last_analyze ? new Date(t.last_analyze).toLocaleDateString() : t.last_autoanalyze ? 'auto' : '—'}
                    </Text>
                  </Tooltip>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}


// ── Active Queries Tab ──────────────────────────────────────────────────────

function ActiveQueriesTab() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/postgres/queries/`, { headers: getAuthHeaders() })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => { if (data.error) throw new Error(data.error); setQueries(data.queries); setError(null); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => {
    fetch_();
    const interval = setInterval(fetch_, 5000);
    return () => clearInterval(interval);
  }, [fetch_]);

  if (loading && queries.length === 0) return <Box textAlign="center" py={16}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  const stateColor = (state) => {
    switch (state) {
      case 'active': return 'green';
      case 'idle': return 'gray';
      case 'idle in transaction': return 'yellow';
      case 'idle in transaction (aborted)': return 'red';
      default: return 'gray';
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text color="gray.500">{queries.length} connections &middot; Refreshes every 5s</Text>
        <Button leftIcon={<FiRefreshCw />} size="sm" onClick={fetch_}>Refresh</Button>
      </HStack>

      {queries.length === 0 ? (
        <Alert status="info" borderRadius="lg"><AlertIcon />No active connections (other than this one).</Alert>
      ) : (
        <Box bg="white" borderRadius="lg" boxShadow="md" overflow="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>PID</Th>
                <Th>User</Th>
                <Th>State</Th>
                <Th>Duration</Th>
                <Th>Wait</Th>
                <Th>Client</Th>
                <Th>Query</Th>
              </Tr>
            </Thead>
            <Tbody>
              {queries.map(q => (
                <Tr key={q.pid} _hover={{ bg: 'gray.50' }}>
                  <Td><Text fontSize="sm" fontFamily="mono">{q.pid}</Text></Td>
                  <Td><Text fontSize="sm">{q.username}</Text></Td>
                  <Td><Badge colorScheme={stateColor(q.state)} fontSize="xs">{q.state}</Badge></Td>
                  <Td><Text fontSize="xs" fontFamily="mono">{q.duration || '—'}</Text></Td>
                  <Td>
                    {q.wait_event ? (
                      <Tooltip label={`${q.wait_event_type}: ${q.wait_event}`}>
                        <Badge colorScheme="orange" fontSize="xs">{q.wait_event}</Badge>
                      </Tooltip>
                    ) : (
                      <Text fontSize="xs" color="gray.400">—</Text>
                    )}
                  </Td>
                  <Td><Text fontSize="xs" color="gray.500">{q.client_addr || 'local'}</Text></Td>
                  <Td>
                    <Tooltip label={q.query} placement="top-start">
                      <Text fontSize="xs" fontFamily="mono" color="gray.600" isTruncated maxW="400px">{q.query}</Text>
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}


// ── Query Tab ───────────────────────────────────────────────────────────────

function QueryTab() {
  const [sql, setSql] = useState('SELECT * FROM pg_stat_user_tables ORDER BY n_live_tup DESC;');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const runQuery = async () => {
    if (!sql.trim()) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/postgres/query/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sql: sql.trim(), limit: 200 }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        toast({ title: 'Query failed', description: data.error, status: 'error', duration: 5000 });
      } else {
        setResult(data);
      }
    } catch (err) {
      setError(err.message);
    }
    setRunning(false);
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      runQuery();
    }
  };

  return (
    <Box>
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <HStack justify="space-between" mb={3}>
          <VStack align="start" spacing={0}>
            <Heading size="sm" color="gray.700">SQL Query</Heading>
            <Text fontSize="xs" color="gray.500">Read-only queries only (SELECT, EXPLAIN, SHOW, WITH)</Text>
          </VStack>
          <Button
            leftIcon={<FiPlay />}
            colorScheme="green"
            size="sm"
            onClick={runQuery}
            isLoading={running}
          >
            Run (⌘+Enter)
          </Button>
        </HStack>
        <Textarea
          value={sql}
          onChange={e => setSql(e.target.value)}
          onKeyDown={handleKeyDown}
          fontFamily="mono"
          fontSize="sm"
          bg="gray.800"
          color="green.300"
          border="none"
          rows={6}
          placeholder="SELECT * FROM ..."
          _placeholder={{ color: 'gray.500' }}
          borderRadius="md"
        />
      </Box>

      {error && (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Code colorScheme="red" fontSize="sm" whiteSpace="pre-wrap">{error}</Code>
        </Alert>
      )}

      {result && (
        <Box bg="white" borderRadius="lg" boxShadow="md" overflow="auto">
          <HStack justify="space-between" px={4} py={3} bg="gray.50" borderBottom="1px" borderColor="gray.200">
            <Text fontSize="sm" color="gray.600">
              {result.row_count} row{result.row_count !== 1 ? 's' : ''} returned
              {result.truncated && <Badge colorScheme="yellow" ml={2}>truncated</Badge>}
            </Text>
            <Text fontSize="xs" color="gray.400">{result.columns.length} columns</Text>
          </HStack>
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                {result.columns.map((col, i) => (
                  <Th key={i} fontSize="xs">{col}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {result.rows.map((row, ri) => (
                <Tr key={ri} _hover={{ bg: 'gray.50' }}>
                  {row.map((cell, ci) => (
                    <Td key={ci}>
                      <Tooltip label={cell} placement="top-start">
                        <Text fontSize="xs" fontFamily="mono" isTruncated maxW="250px">
                          {cell ?? <Text as="span" color="gray.400" fontStyle="italic">NULL</Text>}
                        </Text>
                      </Tooltip>
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}


// ── Main Component ──────────────────────────────────────────────────────────

function PostgresStatus() {
  return (
    <Box>
      <VStack align="start" spacing={1} mb={6}>
        <Heading size="lg">PostgreSQL</Heading>
        <Text color="gray.500">Database status, tables, active queries, and SQL console</Text>
      </VStack>

      <Tabs colorScheme="purple" variant="enclosed">
        <TabList>
          <Tab><HStack spacing={2}><Icon as={FiDatabase} boxSize={4} /><Text>Overview</Text></HStack></Tab>
          <Tab><HStack spacing={2}><Icon as={FiHardDrive} boxSize={4} /><Text>Tables</Text></HStack></Tab>
          <Tab><HStack spacing={2}><Icon as={FiActivity} boxSize={4} /><Text>Active Queries</Text></HStack></Tab>
          <Tab><HStack spacing={2}><Icon as={FiPlay} boxSize={4} /><Text>Query</Text></HStack></Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt={6}><OverviewTab /></TabPanel>
          <TabPanel px={0} pt={6}><TablesTab /></TabPanel>
          <TabPanel px={0} pt={6}><ActiveQueriesTab /></TabPanel>
          <TabPanel px={0} pt={6}><QueryTab /></TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default PostgresStatus;
